import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Input, Space, Modal, Form, Switch, message, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, LockOutlined, UnlockOutlined } from '@ant-design/icons';
import { Get, Post, Put, DeleteSingle as Del } from '../../utils/httpProvider';
import { DON_VI_API } from '../../utils/apiUrl';

const { Search } = Input;

const DonViList = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [keyword, setKeyword] = useState('');

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [form] = Form.useForm();
    const [editingId, setEditingId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadData();
    }, [pageIndex, pageSize, keyword]);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await Get(DON_VI_API.GET_PAGING, null, {
                PageIndex: pageIndex,
                PageSize: pageSize,
                Keyword: keyword
            });
            if (res && res.data) {
                setData(res.data);
                setTotalItems(res.totalRecords || 0);
            }
        } catch (error) {
            message.error('Lỗi khi tải dữ liệu đơn vị');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setKeyword(value);
        setPageIndex(1);
    };

    const showModal = (mode, record = null) => {
        setModalMode(mode);
        setIsModalVisible(true);
        if (mode === 'edit' && record) {
            setEditingId(record.id);
            form.setFieldsValue({
                tenDonVi: record.tenDonVi,
                maDonVi: record.maDonVi,
                isActive: record.isActive
            });
        } else {
            setEditingId(null);
            form.resetFields();
            form.setFieldsValue({ isActive: 1 });
        }
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        form.resetFields();
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);

            // Kiểm tra trùng tên
            const checkRes = await Get(DON_VI_API.GET_PAGING, null, { PageIndex: 1, PageSize: 1000 });
            if (checkRes && checkRes.data) {
                const nameLower = values.tenDonVi.trim().toLowerCase();
                const isDuplicate = checkRes.data.some(item => 
                    item.tenDonVi && item.tenDonVi.trim().toLowerCase() === nameLower && 
                    (modalMode === 'add' || item.id !== editingId)
                );
                if (isDuplicate) {
                    message.error('Tên đơn vị này đã tồn tại trong danh mục!');
                    setSubmitting(false);
                    return;
                }
            }

            const currentRecord = data.find(item => item.id === editingId);
            const currentActive = currentRecord ? (currentRecord.isActive === true || currentRecord.isActive === 1 || String(currentRecord.isActive).trim().toLowerCase() === 'true') : true;

            const payload = {
                ...values,
                isActive: modalMode === 'add' ? 1 : (currentActive ? 1 : 0),
                loaiDonVi: currentRecord?.loaiDonVi || 'Khac'
            };

            if (modalMode === 'add') {
                await Post(DON_VI_API.ADD_ITEM, null, payload);
                message.success('Thêm mới thành công');
            } else {
                const editUrl = DON_VI_API.EDIT_ITEM.includes('{id}') 
                    ? DON_VI_API.EDIT_ITEM.replace('{id}', editingId) 
                    : `${DON_VI_API.EDIT_ITEM}/${editingId}`;
                await Put(editUrl, null, payload);
                message.success('Cập nhật thành công');
            }
            setIsModalVisible(false);
            loadData();
        } catch (error) {
            console.error(error);
            const errorMsg = error && typeof error === 'object' && error.message ? error.message : (typeof error === 'string' ? error : 'Có lỗi xảy ra');
            message.error(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleStatus = async (record) => {
        try {
            const currentActive = record.isActive === true || record.isActive === 1 || String(record.isActive).trim().toLowerCase() === 'true';
            const payload = {
                ...record,
                isActive: currentActive ? 0 : 1,
                loaiDonVi: record.loaiDonVi || 'Khac'
            };
            const editUrl = DON_VI_API.EDIT_ITEM.includes('{id}') 
                ? DON_VI_API.EDIT_ITEM.replace('{id}', record.id) 
                : `${DON_VI_API.EDIT_ITEM}/${record.id}`;
            await Put(editUrl, null, payload);
            message.success('Cập nhật trạng thái thành công');
            loadData();
        } catch (error) {
            message.error('Cập nhật trạng thái thất bại');
        }
    };

    const handleDelete = async (id) => {
        try {
            const deleteUrl = DON_VI_API.DELETE_ITEM.includes('{id}') 
                ? DON_VI_API.DELETE_ITEM.replace('{id}', id) 
                : `${DON_VI_API.DELETE_ITEM}/${id}`;
            await Del(deleteUrl);
            message.success('Xóa thành công');
            loadData();
        } catch (error) {
            message.error('Xóa thất bại');
        }
    };

    const columns = [
        {
            title: 'Tên đơn vị',
            dataIndex: 'tenDonVi',
            key: 'tenDonVi',
        },
        {
            title: 'Tên tắt',
            dataIndex: 'maDonVi',
            key: 'maDonVi',
        },
        {
            title: 'Trạng thái',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (
                <Tag color={isActive ? 'success' : 'default'}>
                    {isActive ? 'Hoạt động' : 'Tạm khóa'}
                </Tag>
            )
        },
        {
            title: 'Thao tác',
            key: 'action',
            render: (_, record) => (
                <Space size="middle">
                    <Popconfirm
                        title={`Bạn có chắc chắn muốn ${record.isActive ? 'khóa' : 'mở khóa'}?`}
                        onConfirm={() => handleToggleStatus(record)}
                        okText="Có"
                        cancelText="Không"
                    >
                        <Button type="link" icon={record.isActive ? <LockOutlined /> : <UnlockOutlined />}>
                            {record.isActive ? 'Khóa' : 'Mở khóa'}
                        </Button>
                    </Popconfirm>
                    <Button type="link" icon={<EditOutlined />} onClick={() => showModal('edit', record)}>
                        Sửa
                    </Button>
                    <Popconfirm
                        title="Bạn có chắc chắn muốn xóa?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Có"
                        cancelText="Không"
                    >
                        <Button type="link" danger icon={<DeleteOutlined />}>
                            Xóa
                        </Button>
                    </Popconfirm>
                </Space>
            ),
            width: 250
        }
    ];

    return (
        <Card title="Danh mục Đơn vị" extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal('add')}>
                Thêm mới
            </Button>
        }>
            <div style={{ marginBottom: 16 }}>
                <Search
                    placeholder="Tìm kiếm theo tên, tên tắt..."
                    allowClear
                    onSearch={handleSearch}
                    style={{ width: 300 }}
                />
            </div>

            <Table
                columns={columns}
                dataSource={data}
                rowKey="id"
                loading={loading}
                scroll={{ x: 'max-content' }}
                pagination={{
                    current: pageIndex,
                    pageSize: pageSize,
                    total: totalItems,
                    onChange: (page, size) => {
                        setPageIndex(page);
                        setPageSize(size);
                    }
                }}
            />

            <Modal
                title={modalMode === 'add' ? 'Thêm mới đơn vị' : 'Cập nhật đơn vị'}
                open={isModalVisible}
                onOk={handleSubmit}
                onCancel={handleCancel}
                confirmLoading={submitting}
                okText="Lưu"
                cancelText="Hủy"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="tenDonVi"
                        label="Tên đơn vị"
                        rules={[{ required: true, message: 'Vui lòng nhập tên đơn vị' }]}
                    >
                        <Input placeholder="Nhập tên đơn vị" />
                    </Form.Item>
                    <Form.Item
                        name="maDonVi"
                        label="Tên tắt"
                        rules={[{ required: true, message: 'Vui lòng nhập tên tắt' }]}
                    >
                        <Input placeholder="Nhập tên tắt" />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default DonViList;
